import { SectionLabel } from '../../../design/components'
import { useCampaignWorld } from '../../campaigns/queries'
import styles from '../../../routes/play/play.module.css'

export interface LocationPickerProps {
  campaignId: number
  /** The chosen location, or null for "nowhere in particular". */
  value: number | null
  onChange: (locationId: number | null) => void
  /** Shown under the field. The live-session hint, mostly. */
  hint?: string | undefined
  /** Held while a save is in flight, so a second change cannot race the first. */
  disabled?: boolean | undefined
}

/**
 * Where the fight happens.
 *
 * Optional on purpose: an encounter is planned long before anyone knows where it lands, and a
 * required field here would make the common case — "a brawl, somewhere, later" — impossible to
 * express. Null is a real answer and the list says so in words rather than leaving a blank row.
 *
 * **Each place says whether it has a battle map**, because that is the thing the choice actually
 * decides. A live session runs on a location's map, so picking a place without one gets you an
 * encounter that cannot be run live — and finding that out here, while choosing, is a great deal
 * better than finding out on the encounter screen afterwards.
 */
export function LocationPicker({
  campaignId,
  value,
  onChange,
  hint,
  disabled = false,
}: LocationPickerProps) {
  const world = useCampaignWorld(campaignId)
  const locations = world.data?.sections.locations?.locations ?? []

  return (
    <label className={styles.field}>
      <SectionLabel tone="muted" micro>
        Where it happens
      </SectionLabel>

      <select
        className={styles.select}
        value={value === null ? '' : String(value)}
        disabled={disabled || world.isPending}
        onChange={(event) =>
          onChange(event.target.value === '' ? null : Number(event.target.value))
        }
      >
        <option value="">Nowhere in particular</option>
        {locations.map((location) => (
          <option key={location.id} value={location.id}>
            {location.name}
            {location.battleMapImageUrl === null ? '' : ' · has a battle map'}
          </option>
        ))}
      </select>

      {world.isPending ? (
        <p className={styles.note}>Reading the city…</p>
      ) : locations.length === 0 ? (
        <p className={styles.note}>
          This campaign&apos;s city has no locations yet. Add one on the city page and it will
          appear here.
        </p>
      ) : (
        hint !== undefined && <p className={styles.note}>{hint}</p>
      )}
    </label>
  )
}
